import { useEffect, useState } from "react";
import { Calculator } from "lucide-react";
import { fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// < €500 -> recommend +10% | €500–€1,000 -> recommend +5% | > €1,000 -> admin chooses
export const recommendedFeePct = (price) => (price < 500 ? 10 : price <= 1000 ? 5 : null);

const fmtEUR = (v) => {
  const r = Math.round(v * 100) / 100;
  return `€${r.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 })}`;
};

export default function EuroCalculator() {
  const [price, setPrice] = useState("");
  const [rate, setRate] = useState(""); // digits only; displayed with Indonesian thousands dots
  const [fee, setFee] = useState(null); // 5 | 10 | null
  const [extra, setExtra] = useState("");

  const priceNum = parseFloat(price);
  const hasPrice = price !== "" && !Number.isNaN(priceNum) && priceNum >= 0;
  const rateNum = parseInt(rate || "", 10);
  const hasRate = rate !== "" && !Number.isNaN(rateNum) && rateNum > 0;
  const extraNum = extra === "" ? 0 : Math.max(0, parseInt(extra, 10) || 0);

  const recommended = hasPrice ? recommendedFeePct(priceNum) : null;

  // auto-preselect the recommended fee when the price range changes (recommendation, never locked)
  const range = !hasPrice ? "none" : priceNum < 500 ? "low" : priceNum <= 1000 ? "mid" : "high";
  useEffect(() => {
    if (range === "low") setFee(10);
    else if (range === "mid") setFee(5);
    // "high": no recommendation — admin picks +5% or +10% explicitly
  }, [range]);

  const hasFee = fee != null;
  const eurWithFee = hasPrice && hasFee ? priceNum * (1 + fee / 100) : null;
  const convertedIDR = eurWithFee != null && hasRate ? eurWithFee * rateNum : null;
  const finalIDR = convertedIDR != null ? convertedIDR + extraNum : null;

  const FeeButton = ({ pct }) => (
    <button
      type="button"
      data-testid={`euro-fee-${pct}`}
      onClick={() => setFee(pct)}
      className={`flex h-12 flex-col items-center justify-center rounded-md border text-sm font-extrabold transition-colors active:scale-[0.98] ${
        fee === pct ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
      }`}
    >
      <span>+{pct}%</span>
      {recommended === pct && (
        <span className={`text-[9px] font-bold tracking-wide ${fee === pct ? "text-white/80" : "text-primary"}`}>RECOMMENDED +{pct}%</span>
      )}
    </button>
  );

  return (
    <div data-testid="euro-calculator" className="overflow-hidden rounded-lg border bg-card">
      <div className="flex items-center gap-2.5 border-b px-4 py-3" style={{ backgroundColor: "#D8C9BE" }}>
        <Calculator className="h-4 w-4 shrink-0" style={{ color: "#4E4542" }} />
        <div>
          <h2 className="font-heading text-sm font-extrabold tracking-wide" style={{ color: "#4E4542" }}>🇪🇺 EUROPE CALCULATOR</h2>
          <p className="text-[11px]" style={{ color: "#6B615C" }}>Standalone — no Europe batch needed</p>
        </div>
      </div>
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">ITEM PRICE (EUR)</Label>
            <NumInput
              data-testid="euro-price-input"
              mode="decimal"
              className="h-12"
              placeholder="0.00"
              value={price}
              onValue={setPrice}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold">EURO RATE (Rp / €1)</Label>
            <NumInput
              data-testid="euro-rate-input"
              mode="money"
              className="h-12"
              placeholder="e.g. 17.500"
              value={rate}
              onValue={setRate}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-bold">
            FEE % {range === "high" && hasPrice ? "— choose one" : "(recommended, adjustable)"}
          </Label>
          <div className="grid grid-cols-2 gap-3" data-testid="euro-fee-options">
            <FeeButton pct={5} />
            <FeeButton pct={10} />
          </div>
          {range === "high" && fee == null && (
            <p className="text-[11px] font-semibold text-amber-600" data-testid="euro-fee-choose-hint">Item &gt; €1.000 — please choose +5% or +10%.</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-bold">EXTRA CHARGE (Rp)</Label>
          <NumInput
            data-testid="euro-extra-charge-input"
            mode="money"
            className="h-12"
            placeholder="0"
            value={extra}
            onValue={setExtra}
          />
        </div>

        <div data-testid="euro-pricing-guide" className="rounded-md border border-dashed p-3" style={{ backgroundColor: "#EDE4DE" }}>
          <p className="mb-2 text-[11px] font-extrabold tracking-wide text-stone-500">EUROPE PRICING GUIDE</p>
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-stone-600">General item &lt; €500</span>
              <span className="font-semibold text-stone-800">Recommended Fee: +10%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-600">General item €500–€1.000</span>
              <span className="font-semibold text-stone-800">Recommended Fee: +5%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-600">General item &gt; €1.000</span>
              <span className="font-semibold text-stone-800">Choose +5% / +10%</span>
            </div>
            <div className="my-1.5 border-t border-dashed" />
            <div className="flex items-center justify-between" data-testid="euro-guide-longchamp">
              <span className="text-stone-600">Longchamp</span>
              <span className="font-semibold text-stone-800">Extra Charge: +Rp500.000</span>
            </div>
            <div className="flex items-center justify-between" data-testid="euro-guide-polene">
              <span className="text-stone-600">Polène</span>
              <span className="font-semibold text-stone-800">Extra Charge: +Rp1.000.000</span>
            </div>
          </div>
          <p className="mt-2 text-[10px] italic text-muted-foreground">Panduan saja — Extra Charge diisi manual oleh Admin bila diperlukan.</p>
        </div>

        <div className="space-y-1.5 border-t pt-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">ITEM PRICE</span>
            <span data-testid="euro-item-price-display" className="font-bold">{hasPrice ? fmtEUR(priceNum) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">SELECTED FEE</span>
            <span data-testid="euro-selected-fee" className="font-bold">{hasFee ? `+${fee}%` : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">EUR WITH FEE</span>
            <span data-testid="euro-with-fee" className="font-bold">{eurWithFee != null ? fmtEUR(eurWithFee) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">CONVERTED TO IDR</span>
            <span data-testid="euro-converted-idr" className="font-bold">{convertedIDR != null ? fmtIDR(convertedIDR) : "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">EXTRA CHARGE</span>
            <span data-testid="euro-extra-charge-display" className="font-bold">{extra !== "" ? fmtIDR(extraNum) : "Rp0"}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between rounded-md border border-[#C7D5DB] px-3 py-2.5" style={{ backgroundColor: "#DCE5E9" }}>
            <span className="text-xs font-extrabold tracking-wide" style={{ color: "#3F5560" }}>FINAL H&T COST</span>
            <span data-testid="euro-final-cost" className="font-heading text-xl font-extrabold" style={{ color: "#2F4A57" }}>
              {finalIDR != null ? fmtIDR(finalIDR) : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
